import type { DesignLayer, ResolvedDesignAreaConfig } from '@pz/scene';
import { el, hexOf } from '../panels.js';
import { phoneIconSvg, type PhoneIconName } from './icons.js';
import type { PhoneTexts } from './texts-phone.js';

/**
 * The bottom grid: four cells per row, label above icon, exactly the lists in
 * the spec's "Mobile property grid" row.
 *
 * The lists themselves are a pure function of the selected layer's kind and
 * what the zone allows — no DOM, no store — so they are the part the tests
 * pin down. Zazzle shows Add and Preview as the first two cells of the same
 * scrollable grid, then a divider, then the object's own properties; Delete
 * and Duplicate sit right after the divider for every kind, which is why they
 * are a shared prefix here rather than repeated per kind.
 */

export type PhoneGridKind = 'none' | 'text' | 'image' | 'artwork' | 'shape' | 'qr';

export type PhoneGridItem =
  | 'add' | 'preview' | 'divider'
  | 'delete' | 'duplicate'
  | 'edit' | 'font' | 'size' | 'color' | 'align' | 'textWidth' | 'spacing' | 'lineHeight' | 'curve'
  | 'orientation' | 'rotate' | 'flip' | 'arrange' | 'pattern' | 'textStroke' | 'shadow'
  | 'replace' | 'eraser' | 'crop' | 'fitZone' | 'fillZone' | 'filters' | 'opacity' | 'cutout'
  | 'shapeFill' | 'shapeLine' | 'shapeWidth'
  | 'recolor'
  | 'qrEdit'
  | 'layers' | 'background' | 'templates';

/** What the zone allows, read once from the resolved config. */
export interface PhoneGridCaps {
  pattern: boolean;
  shadow: boolean;
  curve: boolean;
  recolor: boolean;
  removeBackground: boolean;
  filters: boolean;
  background: boolean;
  templates: boolean;
}

export interface PhoneGridContext {
  kind: PhoneGridKind;
  /** Vertical text stacks glyphs, and the renderer ignores `bend` then. */
  vertical?: boolean;
  /** False for a bent, vertical or grouped line: no wrap column is offered. */
  wrappable?: boolean;
  /** True when the text already IS a wrap column, which rules the curve out. */
  fixedBox?: boolean;
  /**
   * `interactive === false` on the layer: the shop authored it and the shopper
   * may not touch it at all, so there are no properties to show (`commands.run`
   * refuses the edits anyway — this is the UI saying so first).
   */
  editable?: boolean;
  /** `permanent` layers stay in the design; Obriši is not offered. */
  deletable?: boolean;
  caps: PhoneGridCaps;
}

/** Add · Pregled · divider, then Obriši · Kopiraj for every selected object. */
const COMMON: readonly PhoneGridItem[] = ['add', 'preview', 'divider'];
const SELECTED: readonly PhoneGridItem[] = ['delete', 'duplicate'];

/**
 * The cells for one selection. Order is the spec's order, and an item the zone
 * forbids is dropped rather than shown disabled — a phone row is too narrow to
 * waste a cell on something that can never be pressed.
 */
export function propertyGridItems(context: PhoneGridContext): PhoneGridItem[] {
  const caps = context.caps;
  const items: PhoneGridItem[] = [...COMMON];
  if (context.kind === 'none') {
    items.push('layers');
    if (caps.background) items.push('background');
    if (caps.templates) items.push('templates');
    return items;
  }
  // A non-interactive object prints as authored: Dodaj and Pregled, nothing
  // else — not even the divider, which would announce a row that never comes.
  if (context.editable === false) return ['add', 'preview'];
  items.push(...SELECTED.filter((item) => item !== 'delete' || context.deletable !== false));
  switch (context.kind) {
    case 'text':
      items.push('edit', 'font', 'size', 'color', 'align');
      // A wrap column only makes sense for a straight, horizontal line; a bent
      // or vertical one has no width to pour words into.
      if (context.wrappable !== false) items.push('textWidth');
      items.push('spacing', 'lineHeight');
      // A wrap column cannot also be an arc (the model refuses the pair).
      if (caps.curve && !context.vertical && !context.fixedBox) items.push('curve');
      items.push('orientation', 'rotate', 'flip', 'arrange');
      if (caps.pattern) items.push('pattern');
      items.push('textStroke');
      if (caps.shadow) items.push('shadow');
      return items;
    case 'image':
      items.push('replace', 'eraser', 'crop', 'fitZone', 'fillZone');
      if (caps.filters) items.push('filters');
      items.push('opacity', 'rotate', 'flip', 'arrange');
      if (caps.pattern) items.push('pattern');
      if (caps.removeBackground) items.push('cutout');
      return items;
    case 'artwork':
      if (caps.recolor) items.push('recolor');
      items.push('opacity', 'rotate', 'flip', 'arrange');
      if (caps.pattern) items.push('pattern');
      return items;
    case 'shape':
      items.push('shapeFill', 'shapeLine', 'shapeWidth', 'opacity', 'rotate', 'flip', 'arrange');
      if (caps.pattern) items.push('pattern');
      return items;
    case 'qr':
      // A QR code prints as pure black on white at full opacity, and a mirrored
      // code does not scan; the model refuses both, so the cells are not shown.
      items.push('qrEdit', 'rotate', 'arrange');
      return items;
  }
}

/** The zone's rules as the grid needs them. */
export function phoneGridCaps(
  config: ResolvedDesignAreaConfig,
  options: { removeBackground: boolean; templates: boolean },
): PhoneGridCaps {
  return {
    pattern: config.allow.pattern,
    shadow: config.text.allowShadow,
    curve: config.text.allowCurve,
    recolor: config.artwork.allowRecolor,
    removeBackground: config.allow.image && config.image.allowRemoveBackground && options.removeBackground,
    filters: config.image.allowFilters,
    background: config.allow.background,
    templates: options.templates,
  };
}

/** Which kind of grid a layer asks for. */
export function gridKindOf(layer: DesignLayer | null): PhoneGridKind {
  if (!layer) return 'none';
  switch (layer.kind) {
    case 'text': return 'text';
    case 'image': return 'image';
    case 'shape': return 'shape';
    case 'qr': return 'qr';
    default: return 'artwork';
  }
}

const ICONS: Record<Exclude<PhoneGridItem, 'divider'>, PhoneIconName> = {
  add: 'plus',
  preview: 'eye',
  delete: 'trash',
  duplicate: 'copy',
  edit: 'pencil',
  font: 'font',
  size: 'size',
  color: 'droplet',
  align: 'alignCenter',
  textWidth: 'textWidth',
  spacing: 'spacing',
  lineHeight: 'lineHeight',
  curve: 'curve',
  orientation: 'orientation',
  rotate: 'rotate',
  flip: 'flipH',
  arrange: 'layers',
  pattern: 'pattern',
  textStroke: 'outline',
  shadow: 'shadow',
  replace: 'swap',
  crop: 'crop',
  eraser: 'pencil',
  fitZone: 'fitZone',
  fillZone: 'fillZone',
  filters: 'filters',
  opacity: 'opacity',
  cutout: 'magic',
  shapeFill: 'bucket',
  shapeLine: 'weight',
  shapeWidth: 'weight',
  recolor: 'droplet',
  qrEdit: 'pencil',
  layers: 'layers',
  background: 'backdrop',
  templates: 'copy',
};

/** The Serbian label for one cell. */
export function gridLabel(item: Exclude<PhoneGridItem, 'divider'>, texts: PhoneTexts): string {
  const table: Record<Exclude<PhoneGridItem, 'divider'>, string> = {
    add: texts.add,
    preview: texts.preview,
    delete: texts.delete,
    duplicate: texts.duplicate,
    edit: texts.edit,
    font: texts.font,
    size: texts.size,
    color: texts.color,
    align: texts.align,
    textWidth: texts.textWidth,
    spacing: texts.spacing,
    lineHeight: texts.lineHeight,
    curve: texts.curve,
    orientation: texts.orientation,
    rotate: texts.rotate,
    flip: texts.flip,
    arrange: texts.arrange,
    pattern: texts.pattern,
    textStroke: texts.textStroke,
    shadow: texts.shadow,
    replace: texts.replace,
    crop: texts.crop,
    eraser: 'Gumica',
    fitZone: texts.fitZone,
    fillZone: texts.fillZone,
    filters: texts.filters,
    opacity: texts.opacity,
    cutout: texts.cutout,
    shapeFill: texts.shapeFill,
    shapeLine: texts.shapeLine,
    shapeWidth: texts.shapeWidth,
    recolor: texts.recolor,
    qrEdit: texts.qrEdit,
    layers: texts.layers,
    background: texts.background,
    templates: texts.templates,
  };
  return table[item];
}

export interface PropertyGridOptions {
  texts: PhoneTexts;
  onItem(item: Exclude<PhoneGridItem, 'divider'>): void;
}

export interface PropertyGridHandle {
  element: HTMLElement;
  /** Redraws the cells for a new selection. */
  render(context: PhoneGridContext, layer: DesignLayer | null, openId?: string | null): void;
}

/**
 * The grid itself. Every cell carries a stable class and an aria-label, so the
 * lead can drive it from the DOM in an emulated phone where screenshots are
 * unreliable, and every cell is a real button that a mouse can click.
 */
export function createPropertyGrid(options: PropertyGridOptions): PropertyGridHandle {
  const wrap = el('div', 'pzd-ph-scroll');
  const grid = el('div', 'pzd-ph-grid');
  grid.setAttribute('role', 'toolbar');
  grid.setAttribute('aria-label', options.texts.gridLabel);
  const hint = el('div', 'pzd-ph-hint', options.texts.nothingSelected);
  hint.setAttribute('role', 'status');
  wrap.append(grid, hint);

  function cell(item: Exclude<PhoneGridItem, 'divider'>, layer: DesignLayer | null, openId?: string | null): HTMLButtonElement {
    const label = gridLabel(item, options.texts);
    const button = el('button', `pzd-ph-cell is-${item}`);
    button.type = 'button';
    button.dataset.item = item;
    button.setAttribute('aria-label', label);
    button.title = label;
    button.append(el('small', undefined, label));
    const icon = el('i');
    icon.innerHTML = phoneIconSvg(ICONS[item], 19);
    button.append(icon);
    // Zazzle paints the current colour into the Boja cell; ours does the same
    // for text, shape fill and a recoloured artwork, so the grid reads as the
    // state of the object rather than a row of identical glyphs.
    const swatch = currentSwatch(item, layer);
    if (swatch) {
      button.classList.add('is-swatch');
      button.style.setProperty('--pzd-ph-swatch', swatch);
      icon.innerHTML = '';
    }
    if (openId === item) button.classList.add('is-on');
    if (item === 'orientation' && layer?.kind === 'text' && layer.orientation === 'vertical') button.classList.add('is-on');
    button.addEventListener('click', () => options.onItem(item));
    return button;
  }

  return {
    element: wrap,
    render(context, layer, openId) {
      grid.textContent = '';
      for (const item of propertyGridItems(context)) {
        if (item === 'divider') {
          const line = el('span', 'pzd-ph-div');
          line.setAttribute('aria-hidden', 'true');
          grid.append(line);
          continue;
        }
        grid.append(cell(item, layer, openId));
      }
      // One line under the grid: what to do next, or why there is nothing here.
      const locked = context.kind !== 'none' && context.editable === false;
      hint.textContent = locked ? options.texts.notInteractive : options.texts.nothingSelected;
      hint.hidden = context.kind !== 'none' && !locked;
    },
  };
}

/** The colour a cell should show, or an empty string when it shows an icon. */
function currentSwatch(item: PhoneGridItem, layer: DesignLayer | null): string {
  if (!layer) return '';
  if (item === 'color' && layer.kind === 'text') return hexOf(layer.color);
  if (item === 'shapeFill' && layer.kind === 'shape') return hexOf(layer.fill);
  if (item === 'shapeLine' && layer.kind === 'shape') return hexOf(layer.stroke?.color);
  if (item === 'recolor' && layer.kind === 'artwork') return hexOf(layer.singleColor);
  return '';
}
